import React, { useState, useEffect } from "react";
import { MdFormatListBulleted } from "react-icons/md";
import CmsService from "../../../services/CmsService";
import SideBar from "../../../components/Sidebar";
import LoadingScreen from "../../../components/LoadingScreen/LoadingScreen";
import Swal from "sweetalert2";
import SubscriptionDialog from "../../../components/Dialogs/SubscriptionDialog";
import { useSubscriptionCheck } from "../../../hooks/useSubscriptionCheck";
import { useNavigate } from "react-router-dom";

const airtelLogoUrl = "/assets/images/airtel_logo.png";

const CMS = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const { premiumStatus, showDialog, setShowDialog, checkSubscriptionStatus } =
    useSubscriptionCheck();

  useEffect(() => {
    const initialize = async () => {
      setLoading(true);
      try {
        await checkSubscriptionStatus();
      } finally {
        setLoading(false);
      }
    };
    initialize();
  }, []);

  const getUserLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject({ message: "Geolocation is not supported" });
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        (error) => {
          if (error.code === 1)
            reject({ message: "Location permission denied" });
          else if (error.code === 2)
            reject({ message: "Location unavailable" });
          else if (error.code === 3)
            reject({ message: "Location request timed out" });
          else reject({ message: "Failed to get location" });
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
      );
    });
  };

  const handleLogin = async () => {
    if (loading) return;
    setLoading(true);

    try {
      const { latitude, longitude } = await getUserLocation();
      const result = await CmsService.generateUrlAirtelCms(latitude, longitude);

      if (!result.success) {
        Swal.fire("Oops!", result.message || "Request failed!", "error");
        return;
      }

      const res = result.data;
      if (res.status === "SUCCESS") {
        const url = res.data?.url;
        if (!url) {
          Swal.fire("Oops!", "URL not received", "error");
          return;
        }
        window.open(url, "_blank");
      } else {
        Swal.fire("Oops!", res.message || "Something went wrong", "error");
      }
    } catch (error) {
      Swal.fire("Error", error.message || "Something went wrong", "error");
    } finally {
      setLoading(false);
    }
  };

  const services = [
    {
      name: "Airtel CMS",
      icon: (
        <img
          src={airtelLogoUrl}
          alt="Airtel"
          width="40"
          height="40"
          style={{ objectFit: "contain" }}
        />
      ),
      action: handleLogin,
      subtitle: "Initiate transaction",
    },
    {
      name: "Transaction History",
      icon: <MdFormatListBulleted size={40} color="#0d6efd" />,
      action: () => navigate("/cms/history"),
      subtitle: "View recent transactions",
    },
  ];

  return (
    <div>
      {loading && <LoadingScreen />}
      <div className="container-fluid mt-3">
        <div className="row">
          <SideBar />
          <div className="col-md-9 mb-3">
            <div className="card mb-4 shadow-sm">
              <div className="card-body">
                <h5 className="card-title mb-4">
                  Cash Management Services (CMS)
                </h5>
                <div className="d-flex gap-4 flex-wrap justify-content-center">
                  {services.map((service, index) => (
                    <div
                      key={index}
                      className="border rounded p-4 text-center shadow-sm"
                      style={{
                        width: "220px",
                        cursor: loading ? "not-allowed" : "pointer",
                        opacity: loading ? 0.6 : 1,
                        transition: "all 0.3s ease",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                      onClick={() => {
                        if (premiumStatus === "true") {
                          !loading && service.action();
                        } else {
                          setShowDialog(true);
                        }
                      }}
                      onMouseEnter={(e) =>
                        !loading &&
                        (e.currentTarget.style.transform = "scale(1.05)")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.transform = "scale(1)")
                      }
                    >
                      {/* Fixed height container for icons to ensure alignment */}
                      <div
                        className="mb-3 d-flex justify-content-center align-items-center"
                        style={{ height: "60px" }}
                      >
                        {service.icon}
                      </div>
                      <div className="fw-bold">{service.name}</div>
                      <div className="small text-muted mt-1">
                        {service.subtitle}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <SubscriptionDialog
        show={showDialog}
        onHide={() => setShowDialog(false)}
        serviceName="CMS Service"
      />
    </div>
  );
};

export default CMS;
